import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRightIcon, 
  ChevronDownIcon, 
  AdjustmentsVerticalIcon,
  XMarkIcon,
  GlobeAltIcon, 
  AcademicCapIcon, 
  CpuChipIcon,
  PaintBrushIcon, 
  BeakerIcon, 
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  MapPinIcon,
  DevicePhoneMobileIcon, // added
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import TeacherCard from "../components/common/TeacherCard";
import { Helmet } from 'react-helmet';

// Use lazy loading for components that aren't needed immediately
const CourseCard = lazy(() => import('../components/common/CourseCard'));
const CourseCardSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm animate-pulse p-4">
    <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4"></div>
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
    <div className="flex justify-between items-center">
      <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
    </div>
  </div>
);

// Category Icons Component (memoized)
const CategoryIcon = React.memo(({ categoryName }) => {
  const iconMap = {
    "Matematik": <AcademicCapIcon className="w-5 h-5" />,
    "Fizik": <BeakerIcon className="w-5 h-5" />,
    "Kimya": <CpuChipIcon className="w-5 h-5" />,
    "Biyoloji": <GlobeAltIcon className="w-5 h-5" />,
    "Programlama": <ChartBarIcon className="w-5 h-5" />,
    "Sanat": <PaintBrushIcon className="w-5 h-5" />,
  };
  
  return iconMap[categoryName] || <AcademicCapIcon className="w-5 h-5" />;
});

// Main Category Button Component (memoized)
const CategoryButton = React.memo(({ category, isSelected, onClick }) => (
  <button
    onClick={() => onClick(category.id)}
    className={`relative flex-shrink-0 flex flex-col items-center justify-center p-2 sm:p-2.5 rounded-lg border transition-all duration-300 hover:shadow-sm group ${
      isSelected
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}
    aria-selected={isSelected}
  >
    <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full mb-1.5 transition-all duration-300 ${
      isSelected
        ? 'bg-blue-500 text-white'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/20'
    }`}>
      <CategoryIcon categoryName={category.name} />
    </div>
    <span className={`text-xs font-medium transition-colors duration-300 text-center line-clamp-1 ${
      isSelected
        ? 'text-blue-700 dark:text-blue-400'
        : 'text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400'
    }`}>
      {category.name}
    </span>
    {isSelected && (
      <div className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-blue-500"></div>
    )}
  </button>
));

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
 const [categoryAnnouncements, setCategoryAnnouncements] = useState([]);
 const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  // Pagination ve veri yönetimi için geliştirilmiş state'ler
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sliderRef = useRef(null);
  const [teachers, setTeachers] = useState([]);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(true);
  const teacherSliderRef = useRef(null);

  // Dashboard verilerini almak için state ve fetch
  const [dashboardStats, setDashboardStats] = useState({
    studentCount: 0,
    teacherCount: 0,
    categoryCount: 0,
    announcementCount: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Dashboard verilerini fetch et
  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await fetch('/v1/dashboard/counts');
      const data = await response.json();
      
      if (data.success && data.data) {
        setDashboardStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Format sayıları için helper fonksiyon
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M+`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K+`;
    return `${num}+`;
  };

  // Stats card component
  const StatsCard = ({ icon, label, value, gradient, onClick }) => (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 transform hover:-translate-y-1 transition-all duration-300 cursor-pointer"
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyPress={e => { if (e.key === 'Enter') onClick && onClick(); }}
    >
      <div className={`w-12 h-12 mb-4 rounded-lg flex items-center justify-center ${gradient} text-white`}>
        {icon}
      </div>
      <div className="font-bold text-2xl text-gray-900 dark:text-white mb-1">
        {isLoadingStats ? (
          <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        ) : (
          formatNumber(value)
        )}
      </div>
      <div className="text-gray-600 dark:text-gray-400 text-sm">{label}</div>
    </div>
  );

  // Optimized fetch functions with useCallback
  const fetchAnnouncements = useCallback(async (page = 0, resetData = false) => {
    if (page === 0 || resetData) {
      setIsLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const response = await fetch(`/v1/announcements/pageable?page=${page}&size=6`);
      const result = await response.json();
      
      if (result && result.success && result.data) {
        const newContent = result.data.content || [];
        
        if (page === 0 || resetData) {
          setFeaturedCourses(newContent);
        } else {
          setFeaturedCourses(prev => [...prev, ...newContent]);
        }
        
        setHasMore(!result.data.last);
      } else {
        if (page === 0 || resetData) {
          setFeaturedCourses([]);
        }
        setHasMore(false);
      }
    } catch (e) {
      console.error("Error fetching announcements:", e);
      if (page === 0 || resetData) {
        setFeaturedCourses([]);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/v1/categories/root');
      const result = await response.json();
      if (result && result.success && Array.isArray(result.data)) {
        setCategories(result.data);
      } else if (result && result.data && Array.isArray(result.data.data)) {
        setCategories(result.data.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  }, []);

  const fetchSubCategories = useCallback(async () => {
    if (!selectedCategory) {
      setSubCategories([]);
      return;
    }
    try {
      const response = await fetch(`/v1/categories/${selectedCategory}/subcategories`);
      const result = await response.json();
      if (result && result.success && Array.isArray(result.data)) {
        setSubCategories(result.data);
      } else if (result && result.data && Array.isArray(result.data.data)) {
        setSubCategories(result.data.data);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategories([]);
    }
  }, [selectedCategory]);

  // Daha fazla ilan yükleme
  const loadMoreAnnouncements = useCallback(() => {
    if (!isLoading && !loadingMore && hasMore) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  }, [isLoading, loadingMore, hasMore]);

  // Sayfa değiştiğinde yeni verileri yükle
  useEffect(() => {
    fetchAnnouncements(currentPage, currentPage === 0);
  }, [currentPage, fetchAnnouncements]);

  // Kategori seçildiğinde filtrele ve sayfayı sıfırla
  const handleCategorySelect = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory(null);
    setActiveCategory(categoryId);
    setCurrentPage(0); // Reset pagination
  }, []);

  const handleSubCategorySelect = useCallback((subCategoryId) => {
    setSelectedSubCategory(subCategoryId);
    setActiveCategory(subCategoryId);
    setCurrentPage(0); // Reset pagination
  }, []);

  // İyileştirilmiş slider gezinme fonksiyonları
  const slideNext = useCallback(() => {
    if (sliderRef.current) {
      const container = sliderRef.current;
      const cardWidth = container.querySelector('.snap-start')?.clientWidth || 300;
      const scrollAmount = cardWidth + 16; // add gap size
      
      // Mevcut scroll pozisyonu ve maksimum scroll genişliği
      const currentScrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      
      // Scroll yapıldıktan sonraki pozisyonu hesapla
      const newScrollLeft = currentScrollLeft + scrollAmount;
      
      // Eğer scroll sonuna yaklaştıysak ve daha fazla veri varsa yeni veri yükle
      if (newScrollLeft >= scrollWidth - clientWidth - (cardWidth * 0.5) && hasMore && !isLoading && !loadingMore) {
        loadMoreAnnouncements();
      }
      
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [hasMore, isLoading, loadingMore, loadMoreAnnouncements]);

  const slidePrev = useCallback(() => {
    if (sliderRef.current) {
      const container = sliderRef.current;
      const cardWidth = container.querySelector('.snap-start')?.clientWidth || 300;
      const scrollAmount = cardWidth + 16; // add gap size
      
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  }, []);
  
  // Alt kategori seçimine göre filtreleme

  const fetchAnnouncementsByCategory = useCallback(async () => {
    if (!selectedSubCategory) return;
    setIsLoadingCategory(true);
    try {
      const response = await fetch(`/v1/announcements/category/${selectedSubCategory}?page=0&size=20`);
      const result = await response.json();
      if (result?.success && result?.data?.content) {
        setCategoryAnnouncements(result.data.content);
      } else if (result?.data?.data?.content) {
        setCategoryAnnouncements(result.data.data.content);
      } else {
        // Sadece fetch başarısızsa boşalt
        setCategoryAnnouncements([]);
      }
    } catch (error) {
      setCategoryAnnouncements([]);
    } finally {
      setIsLoadingCategory(false);
    }
  }, [selectedSubCategory]);
  

  useEffect(() => {
    if (selectedSubCategory) {
      fetchAnnouncementsByCategory();
    }
  }, [selectedSubCategory, fetchAnnouncementsByCategory]);
  const displayedAnnouncements = useMemo(() => {
    if (selectedSubCategory) return categoryAnnouncements;
    return featuredCourses;
  }, [selectedSubCategory, categoryAnnouncements, featuredCourses]);
  // "Tümü" kategorisini seçme ve filtreleri temizleme
  const handleClearFilters = useCallback(() => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setActiveCategory('all');
    setCurrentPage(0);
  }, []);

  // Optimized useEffects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  useEffect(() => {
    fetchAnnouncementsByCategory();
  }, [selectedSubCategory, fetchAnnouncementsByCategory]);

  // Optimized filteredCourses with useMemo
  const filteredCourses = useMemo(() => {
    return featuredCourses.filter(course => {
      if (activeCategory !== 'all' && course.categoryResponse?.name !== activeCategory) {
        return false;
      }
      return true;
    });
  }, [featuredCourses, activeCategory]);

  // Lazy load observer for "Load More" interaction
  const loadMoreButtonRef = useRef(null);

  // Manual loading only - intersection observer disabled
  // Users must click the "Load More" button to load more content
  useEffect(() => {
    // Intersection observer devre dışı - sadece manuel yükleme
    // const observer = new IntersectionObserver(
    //   ([entry]) => {
    //     if (entry.isIntersecting && hasMore && !isLoading && displayedAnnouncements.length > 0) {
    //       setCurrentPage(prev => prev + 1);
    //     }
    //   },
    //   { threshold: 0.5 }
    // );
    
    // if (loadMoreButtonRef.current) {
    //   observer.observe(loadMoreButtonRef.current);
    // }
    
    // return () => {
    //   if (loadMoreButtonRef.current) {
    //     observer.unobserve(loadMoreButtonRef.current);
    //   }
    // };
  }, [hasMore, isLoading, displayedAnnouncements.length]);

  // Öğretmenleri çek
  useEffect(() => {
    const fetchTeachers = async () => {
      setIsLoadingTeachers(true);
      try {
        const res = await fetch('/v1/teacher/all?page=0&size=12');
        const data = await res.json();
        if (data.success && data.data && Array.isArray(data.data.content)) {
          setTeachers(data.data.content);
        } else {
          setTeachers([]);
        }
      } catch {
        setTeachers([]);
      } finally {
        setIsLoadingTeachers(false);
      }
    };
    fetchTeachers();
  }, []);

  // Öğretmen slider navigasyonu
  const slideTeachersNext = () => {
    if (teacherSliderRef.current) {
      teacherSliderRef.current.scrollBy({ left: teacherSliderRef.current.offsetWidth, behavior: 'smooth' });
    }
  };
  const slideTeachersPrev = () => {
    if (teacherSliderRef.current) {
      teacherSliderRef.current.scrollBy({ left: -teacherSliderRef.current.offsetWidth, behavior: 'smooth' });
    }
  };

  // Alt kategori seçili değilse, featuredCourses değişince filteredAnnouncements'ı güncelle
  useEffect(() => {
    if (!selectedSubCategory) {
    }
  }, [featuredCourses, selectedSubCategory]);

  return (
    <div className="font-['Inter'] transition-colors duration-200 dark:bg-gray-900 overflow-x-hidden w-full">
      <Helmet>
        <title>DersMaster - Özel Ders İlan ve Eğitim Platformu</title>
        <meta name="description" content="Türkiye'nin lider özel ders ve eğitim platformu. Alanında uzman öğretmenlerle hemen iletişime geçin, online veya yüz yüze ders alın." />
        <meta name="keywords" content="özel ders, ders ilanı, online eğitim, öğretmen, öğrenci, dersmaster, özel öğretmen, birebir ders, kurs, eğitim platformu, uzaktan eğitim, canlı ders" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="DersMaster" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="DersMaster - Özel Ders İlan ve Eğitim Platformu" />
        <meta property="og:description" content="Türkiye'nin lider özel ders ve eğitim platformu. Alanında uzman öğretmenlerle hemen iletişime geçin, online veya yüz yüze ders alın." />
        <meta property="og:url" content="https://dersmaster.com/" />
        <meta property="og:image" content="https://dersmaster.com/logo192.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DersMaster - Özel Ders İlan ve Eğitim Platformu" />
        <meta name="twitter:description" content="Türkiye'nin lider özel ders ve eğitim platformu. Alanında uzman öğretmenlerle hemen iletişime geçin, online veya yüz yüze ders alın." />
        <meta name="twitter:image" content="https://dersmaster.com/logo192.png" />
        <link rel="canonical" href="https://dersmaster.com/" />
        <html lang="tr" />
      </Helmet>
      {/* Modern decorative elements */}
      <div className="fixed -top-24 -right-24 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none z-0"></div>
      <div className="fixed -bottom-32 -left-32 w-96 h-96 bg-cyan-500/10 dark:bg-cyan-500/5 rounded-full blur-3xl pointer-events-none z-0"></div>
      
      {/* Modernized Hero section with animated gradient */}
      <section className="relative min-h-[90vh] md:min-h-screen flex flex-col overflow-hidden">
        <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-700 dark:to-cyan-600 overflow-hidden">
          {/* Animated mesh gradient overlay */}
          <div className="absolute inset-0 opacity-20">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <radialGradient id="herogradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="50" fill="url(#herogradient)" className="animate-pulse" style={{animationDuration: '8s'}} />
            </svg>
          </div>
          {/* Modern geometric shapes */}
          <div className="absolute -bottom-12 -right-12 w-64 h-64 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute top-20 left-10 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>
          <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-white/10 rounded-full blur-xl"></div>
        </div>

        <div className="w-full mx-auto px-4 sm:px-6 py-8 flex-grow flex flex-col justify-center items-center text-center relative z-10">
          {/* Modern hero content with animated elements */}
          <div className="max-w-4xl w-full">
            <div className="inline-block mb-3 text-white/80 bg-white/10 backdrop-blur-sm px-4 py-1 rounded-full text-sm font-medium animate-fadeIn">
              Türkiye'nin Lider Ders Platformu
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight animate-fadeInUp" style={{animationDelay: '0.2s'}}>
              DersMaster ile <span className="text-yellow-300 relative inline-block">
                Öğrenmeye Başla
              </span> — <span className="md:inline hidden">Alanında</span> Uzman Eğitmenlerle <span className="md:inline hidden">Şimdi</span> Tanış!
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto animate-fadeInUp" style={{animationDelay: '0.4s'}}>
              Binlerce kurs ve uzman eğitmenle becerilerinizi geliştirin. Kariyerinizi ileriye taşıyın.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-fadeInUp" style={{animationDelay: '0.6s'}}>
              <a 
                href="#courses" 
                className="group bg-white text-blue-600 font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-blue-50 transition-all duration-200 transform hover:scale-105 shadow-lg w-full sm:w-auto relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Kurslara Göz At
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-cyan-400 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></span>
              </a>
              <Link 
                to="/register" 
                className="group bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-white/10 transition-all duration-200 w-full sm:w-auto mt-3 sm:mt-0 relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Eğitmen Ol
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 opacity-0 transition-all duration-300 group-hover:opacity-100 -translate-x-3 group-hover:translate-x-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </span>
              </Link>
              {/* New: Google Play download button */}
              <a
                href="https://play.google.com/store/apps/details?id=com.dersmaster.app&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-black text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-gray-900 transition-all duration-200 transform hover:scale-105 shadow-lg w-full sm:w-auto mt-3 sm:mt-0 relative overflow-hidden"
                aria-label="Google Play'den uygulamayı indir"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <DevicePhoneMobileIcon className="h-5 w-5" />
                  Google Play'den İndir
                </span>
              </a>
            </div>
          </div>
          
          {/* Scroll indicator - modernized */}
          <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2">
            <a href="#courses" className="text-white/80 hover:text-white flex flex-col items-center transition-all duration-300 hover:translate-y-2">
              <span className="text-xs sm:text-sm mb-1">Keşfet</span>
              <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-scrollIndicator mt-2"></div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Dashboard Stats Section - Modernized */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-b from-white to-blue-50 dark:from-gray-800 dark:to-gray-900">
        {/* Modern decorative elements */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 -skew-y-6 -translate-y-32 z-0"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-gradient-to-br from-indigo-100/40 to-purple-100/30 dark:from-indigo-900/10 dark:to-purple-900/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-10 w-32 h-32 bg-cyan-100/30 dark:bg-cyan-900/20 rounded-full blur-2xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block mb-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium transform hover:-translate-y-1 transition-all duration-300 shadow-md shadow-blue-500/10">
              Platform Detayları
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
              DersMaster Başarısı
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
              Her gün büyüyen eğitim platformumuzun güncel istatistikleri
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: <UserGroupIcon className="h-7 w-7" />,
                label: "Ziyaretçi",
                value: 10250,
                gradient: "from-blue-400 to-blue-600",
                bgLight: "bg-blue-50",
                bgDark: "dark:bg-blue-900/20",
                textColor: "text-blue-600 dark:text-blue-400",
                onClick: () => {
                  const el = document.getElementById('courses');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }
              },
              {
                icon: <AcademicCapIcon className="h-7 w-7" />,
                label: "Eğitmen",
                value: dashboardStats.teacherCount,
                gradient: "from-violet-400 to-purple-600",
                bgLight: "bg-violet-50",
                bgDark: "dark:bg-violet-900/20",
                textColor: "text-violet-600 dark:text-violet-400",
                onClick: () => {
                  const el = document.getElementById('teachers-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }
              },
              {
                icon: <ChartBarIcon className="h-7 w-7" />,
                label: "Kategori",
                value: dashboardStats.categoryCount,
                gradient: "from-emerald-400 to-green-600",
                bgLight: "bg-emerald-50",
                bgDark: "dark:bg-emerald-900/20",
                textColor: "text-emerald-600 dark:text-emerald-400",
                onClick: () => {
                  const el = document.getElementById('courses');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }
              },
              {
                icon: <DocumentTextIcon className="h-7 w-7" />,
                label: "İlan",
                value: dashboardStats.announcementCount,
                gradient: "from-amber-400 to-orange-600",
                bgLight: "bg-amber-50",
                bgDark: "dark:bg-amber-900/20",
                textColor: "text-amber-600 dark:text-amber-400",
                onClick: () => {
                  const el = document.getElementById('courses');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }
              }
            ].map((stat, index) => (
              <div 
                key={index}
                className={`${stat.bgLight} ${stat.bgDark} rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700/40 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 overflow-hidden relative group cursor-pointer`}
                onClick={stat.onClick}
                tabIndex={0}
                role="button"
                onKeyPress={e => { if (e.key === 'Enter') stat.onClick(); }}
              >
                {/* Top decoration line with gradient */}
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${stat.gradient} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                
                {/* Icon with gradient background and shadow */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${stat.gradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {stat.icon}
                  </div>
                  <h3 className={`text-lg font-bold ${stat.textColor}`}>{stat.label}</h3>
                </div>
                
                {/* Counter value with animation */}
                <div className="relative">
                  {isLoadingStats ? (
                    <div className="flex items-center space-x-2">
                      <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                      <div className="h-5 w-5 rounded-full border-4 border-t-transparent border-blue-600 dark:border-blue-400 animate-spin"></div>
                    </div>
                  ) : (
                    <div className="font-extrabold text-4xl text-gray-800 dark:text-white transition-all duration-700 group-hover:scale-110 group-hover:translate-x-2 flex items-center">
                      {formatNumber(stat.value)}
                      <span className={`ml-2 text-sm font-normal ${stat.textColor} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                        {index === 0 && "aktif ziyaretçi"}
                        {index === 1 && "uzman eğitmen"}
                        {index === 2 && "farklı alanda"}
                        {index === 3 && "güncel ilan"}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Decorative element */}
                <div className={`absolute -bottom-8 -right-8 w-24 h-24 rounded-full ${stat.bgLight} dark:bg-opacity-20 blur-xl opacity-70 transition-opacity duration-300 group-hover:opacity-100`}></div>
              </div>
            ))}
          </div>
          
        </div>
      </section>

      {/* Modern courses section with floating cards */}
      <section id="courses" className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 w-full relative will-change-auto">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/50 dark:bg-blue-900/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-cyan-100/30 dark:bg-cyan-900/10 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 relative">
          {/* Modern section header with underline */}
          <div className="mb-10 sm:mb-14 text-center relative">
            <div className="inline-block text-blue-600 dark:text-blue-400 font-medium bg-blue-100/50 dark:bg-blue-900/30 px-3 py-1 rounded-full text-sm mb-3">
              Özel Dersler
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 relative inline-block">
              Kategorilere Göz At
              <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full"></span>
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mt-5">
              İlgi alanınıza en uygun kursları keşfedin ve öğrenmeye hemen başlayın
            </p>
          </div>

          {/* Category Filter Section */}
          <div className="mb-8 sm:mb-10">
            {/* Mobile filter toggle button */}
            <div className="lg:hidden flex justify-between items-center mb-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-white">Kategoriler</h3>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
              >
                <AdjustmentsVerticalIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium">
                  {showFilters ? 'Gizle' : 'Filtrele'}
                </span>
              </button>
            </div>

            {/* Filter container - responsive */}
            <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden transition-all duration-300 ${!showFilters && 'max-lg:max-h-0 max-lg:opacity-0 max-lg:invisible max-lg:p-0 max-lg:border-0'} lg:visible lg:max-h-none lg:opacity-100 lg:border border-gray-100 dark:border-gray-700`}>
              <div className="p-4">
                {/* Main Categories Section - Horizontal Scroll */}
                <div className="mb-5">
                  <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2.5 px-1">Ana Kategoriler</h3>
                  <div className="relative">
                    {/* Gradient overlays for scroll indication */}
                    <div className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white to-transparent dark:from-gray-800 dark:to-transparent z-10 pointer-events-none"></div>
                    <div className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent dark:from-gray-800 dark:to-transparent z-10 pointer-events-none"></div>
                    
                    {/* Scrollable container */}
                    <div 
                      className="overflow-x-auto flex gap-2 pb-2 scrollbar-thin" 
                      style={{scrollbarWidth: 'thin'}}
                      role="listbox"
                      aria-label="Categories"
                    >
                      {/* All Categories Button */}
                      <button
                        onClick={handleClearFilters}
                        className={`relative flex-shrink-0 flex flex-col items-center justify-center p-2 sm:p-2.5 rounded-lg border transition-all duration-300 hover:shadow-sm group ${
                          activeCategory === 'all'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        }`}
                        role="option"
                        aria-selected={activeCategory === 'all'}
                      >
                        {/* Icon container */}
                        <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full mb-1.5 transition-all duration-300 ${
                          activeCategory === 'all'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/20'
                        }`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                          </svg>
                        </div>
                        
                        {/* Category name */}
                        <span className={`text-xs font-medium transition-colors duration-300 ${
                          activeCategory === 'all'
                            ? 'text-blue-700 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                        }`}>
                          Tümü
                        </span>
                        
                        {/* Selected indicator */}
                        {activeCategory === 'all' && (
                          <div className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-blue-500"></div>
                        )}
                      </button>
                        
                      {/* Optimized category rendering with memoized components */}
                      {categories.map((category) => (
                        <CategoryButton 
                          key={category.id}
                          category={category}
                          isSelected={selectedCategory === category.id}
                          onClick={handleCategorySelect}
                        />
                      ))}   
                    </div>
                  </div>
                </div>

                {/* Optimized Sub Categories Section */}
                <div className={`${subCategories.length > 0 ? 'opacity-100 max-h-32 border-t dark:border-gray-700 pt-4 transition-all duration-300' : 'opacity-0 max-h-0 transition-all duration-300'}`}>
                  {subCategories.length > 0 && (
                    <>
                      <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2.5 px-1 flex items-center">
                        <span className="inline-block mr-1.5 text-blue-500">
                          {categories.find(c => c.id === selectedCategory)?.name}
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="ml-1.5">Alt Kategoriler</span>
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {subCategories.map((subCategory) => (
                          <button
                            key={subCategory.id}
                            onClick={() => handleSubCategorySelect(subCategory.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                              selectedSubCategory === subCategory.id
                                ? 'bg-blue-500 text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {subCategory.name}
                          </button>
                        ))}   
                      </div>
                    </>
                  )}
                </div>
                {/* Filter controls and active filters - OPTIMIZED LAYOUT */}
                <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 ${subCategories.length === 0 ? 'border-t dark:border-gray-700 pt-4' : ''}`}>
                  {/* Active filters display */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {selectedCategory && (
                      <div className="flex items-center bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full text-xs font-medium">
                        <span className="truncate max-w-[100px]">{categories.find(c => c.id === selectedCategory)?.name}</span>
                        <button 
                          onClick={handleClearFilters}
                          className="ml-1 p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {selectedSubCategory && (
                      <div className="flex items-center bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full text-xs font-medium">
                        <span className="truncate max-w-[100px]">{subCategories.find(c => c.id === selectedSubCategory)?.name}</span>
                        <button 
                          onClick={() => {
                            setSelectedSubCategory(null);
                          }}
                          className="ml-1 p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {(selectedCategory || selectedSubCategory) && (
                      <button 
                        onClick={handleClearFilters}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 underline"
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  {/* Sorting control 
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Sırala:</span>
                    <select className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1.5 rounded-lg text-xs border border-gray-200 dark:border-gray-700 focus:ring-1 focus:ring-blue-500/30 appearance-none pr-6 relative bg-no-repeat bg-right bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik02IDcuNUwwIDAuNUgxMkw2IDcuNVoiIGZpbGw9ImN1cnJlbnRDb2xvciIvPgo8L3N2Zz4K')]">
                      <option>En Popüler</option>
                      <option>En Yeniler</option>
                      <option>Fiyat: Düşük-Yüksek</option>
                      <option>Fiyat: Yüksek-Düşük</option>
                    </select>
                  </div>
                  */}
                </div>
              </div>
            </div>
          </div>

          {/* Course display with improved slider */}
          <div className="relative">
            {/* Navigation buttons with improved visibility and accessibility */}
            <button 
              onClick={slidePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-gray-800/90 shadow-lg rounded-r-xl p-2 hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
              aria-label="Önceki ilanlar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              onClick={slideNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-gray-800/90 shadow-lg rounded-l-xl p-2 hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
              aria-label="Sonraki ilanlar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Courses slider with enhanced behavior */}
            <div
  ref={sliderRef}
  className="flex overflow-x-auto gap-4 md:gap-6 snap-x snap-mandatory scrollbar-hide pb-4 scroll-smooth"
  style={{ scrollSnapType: 'x mandatory' }}
>
  {displayedAnnouncements.length === 0 && !isLoading && !isLoadingCategory ? (
    <div className="w-full text-center py-10">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md mx-auto">
        <p className="text-gray-600 dark:text-gray-300">Bu kategoride ilan bulunamadı.</p>
      </div>
    </div>
  ) : (
    <Suspense fallback={null}>
      {displayedAnnouncements.map((course) => (
        <div key={course.id} className="flex-none w-full sm:w-1/2 lg:w-1/3 2xl:w-1/4 snap-start">
          <CourseCard course={course} />
        </div>
      ))}
    </Suspense>
  )}
  {loadingMore && (
    <div className="flex-none w-20 flex items-center justify-center snap-end">
      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  )}
</div>
{/* Loading overlay sadece veri yüklenirken ve eski ilanlar varsa göster */}
{(isLoading || isLoadingCategory) && displayedAnnouncements.length > 0 && (
  <div className="absolute inset-0 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm flex items-center justify-center z-20">
    <div className="p-4 bg-white dark:bg-gray-800 rounded-full shadow-lg">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </div>
)}

            {/* Loading indicator overlay - eski ilanlar kaybolmasın, üstüne overlay gelsin */}
            {isLoading && displayedAnnouncements.length > 0 && (
              <div className="absolute inset-0 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm flex items-center justify-center z-20">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-full shadow-lg">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            )}
          </div>

          {/* View All Courses button - Navigate to courses page */}
          {!isLoading && !loadingMore && displayedAnnouncements.length > 0 && (
            <div className="mt-14 text-center" ref={loadMoreButtonRef}>
              <button 
                className="group relative bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 font-medium px-8 py-4 rounded-xl hover:shadow-lg transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                onClick={() => navigate('/courses')}
                aria-label="Tüm ilanları görüntüle"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span>Tüm İlanları Görüntüle</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-cyan-400 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Modern features section - Moved up */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 w-full relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-grid-gray-200/50 dark:bg-grid-gray-800/20"></div>
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-full blur-3xl"></div>
        
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 relative">
          {/* Modern section header */}
          <div className="text-center mb-14 sm:mb-16 relative">
            <div className="inline-block text-blue-600 dark:text-blue-400 font-medium bg-blue-100/50 dark:bg-blue-900/30 px-3 py-1 rounded-full text-sm mb-3">
              Avantajlar
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 relative inline-block">
              Neden DersMaster?
              <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full"></span>
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mt-5">
              Dersmaster farkıyla eğitim hayatınızı bir üst seviyeye taşıyın
            </p>
          </div>

          {/* Modern features grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            {/* Feature 1 - modernized */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl shadow-blue-100/40 dark:shadow-none border border-gray-100 dark:border-gray-700/50 hover:shadow-2xl hover:shadow-blue-200/30 dark:hover:border-blue-800/30 transition-all duration-300 group">
              <div className="mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-14 h-14 flex items-center justify-center rounded-xl shadow-lg shadow-blue-500/30 dark:shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                Uzman Eğitmenler
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Alanında uzmanlaşmış, deneyimli eğitmenlerle birebir eğitim alma fırsatı.
              </p>
            </div>

            {/* Feature 2 - modernized */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl shadow-blue-100/40 dark:shadow-none border border-gray-100 dark:border-gray-700/50 hover:shadow-2xl hover:shadow-blue-200/30 dark:hover:border-blue-800/30 transition-all duration-300 group">
              <div className="mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-14 h-14 flex items-center justify-center rounded-xl shadow-lg shadow-blue-500/30 dark:shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                Esnek Çalışma Saatleri
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Dilediğiniz zaman diliminde, size uygun saatlerde derslerinizi planlayın.
              </p>
            </div>

            {/* Feature 3 - modernized */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl shadow-blue-100/40 dark:shadow-none border border-gray-100 dark:border-gray-700/50 hover:shadow-2xl hover:shadow-blue-200/30 dark:hover:border-blue-800/30 transition-all duration-300 group md:col-span-2 lg:col-span-1">
              <div className="mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-14 h-14 flex items-center justify-center rounded-xl shadow-lg shadow-blue-500/30 dark:shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                Güvenli Ödeme
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                %100 güvenli ödeme altyapısı ile endişe etmeden eğitiminize odaklanın.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sistemdeki Eğitmenler Section - Moved down */}
      <section id="teachers-section" className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 w-full relative">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 relative">
          {/* Section header with modern style - matches other section headers */}
          <div className="mb-10 sm:mb-14 text-center relative">
            <div className="inline-block text-blue-600 dark:text-blue-400 font-medium bg-blue-100/50 dark:bg-blue-900/30 px-3 py-1 rounded-full text-sm mb-3">
              Uzman Eğitmenler
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 relative inline-block">
              Sistemdeki Eğitmenler
              <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full"></span>
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mt-5">
              Alanında uzman eğitmenlerimizle tanışın
            </p>
          </div>

          {/* Navigation controls - better positioned */}
          <div className="flex justify-end mb-6">
            <div className="flex gap-2">
              <button
                onClick={slideTeachersPrev}
                className="p-2 rounded-full bg-white dark:bg-gray-800 shadow hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                aria-label="Önceki eğitmenler"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={slideTeachersNext}
                className="p-2 rounded-full bg-white dark:bg-gray-800 shadow hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                aria-label="Sonraki eğitmenler"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Teachers slider with better sizing for cards */}
          <div className="relative">
            <div
              ref={teacherSliderRef}
              className="flex overflow-x-auto gap-4 md:gap-6 snap-x snap-mandatory scrollbar-hide pb-6"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {isLoadingTeachers
                ? Array(4).fill(0).map((_, idx) => (
                    <div key={idx} className="flex-none w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 snap-start">
                      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md h-full aspect-[3/4] animate-pulse"></div>
                    </div>
                  ))
                : teachers.length === 0
                  ? (
                    <div className="w-full text-center py-10">
                      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md mx-auto">
                        <p className="text-gray-600 dark:text-gray-400">Sistemde eğitmen bulunamadı.</p>
                      </div>
                    </div>
                  )
                  : teachers.map(teacher => (
                    <div key={teacher.id} className="flex-none w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 snap-start">
                      <TeacherCard teacher={teacher} />
                    </div>
                  ))
              }
            </div>
          </div>

          {/* View All Teachers Button - matches style of the 'Load More' button */}
          <div className="mt-10 text-center">
            <Link 
              to="/teachers"
              className="group relative inline-flex items-center justify-center bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 font-medium px-8 py-4 rounded-xl hover:shadow-lg transition-all duration-300 overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                Tüm Eğitmenleri Görüntüle
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-cyan-400 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Student Assistant - Coming Soon Section */}
<section className="py-10 relative bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
  <div className="container mx-auto px-4 flex flex-col items-center justify-center text-center relative z-10">
    <div className="inline-block mb-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg animate-fadeIn">
      🚀 Devrim Niteliğinde Yenilik
    </div>
    <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mb-3 animate-fadeInUp">
      <span className="text-blue-600 dark:text-blue-400">Yapay Zeka Öğrenci Asistanı</span> Yakında!
    </h2>
    <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto mb-6 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
      Öğrenciler artık kişisel yapay zeka asistanları ile anlık destek alabilecek, gelişimlerini takip edebilecek ve yönlendirmelerle daha hızlı ilerleyebilecekler. 
    </p>
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full font-medium shadow animate-fadeInUp" style={{animationDelay: '0.4s'}}>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v4m0 12v4m8-8h-4M4 12H2" />
      </svg>
      Geliştirme Aşamasında
    </div>
  </div>
</section>

      {/* You might want to add keyframes for the animations */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scrollIndicator {
          0% { transform: translateY(0); opacity: 0; }
          30% { opacity: 1; }
          60% { opacity: 1; }
          100% { transform: translateY(5px); opacity: 0; }
        }
        @keyframes float {
          0% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-10px) translateX(10px); }
          50% { transform: translateY(0) translateX(20px); }
          75% { transform: translateY(10px) translateX(10px); }
          100% { transform: translateY(0) translateX(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 1s ease forwards;
        }
        .animate-fadeInUp {
          animation: fadeInUp 1s ease forwards;
        }
        .animate-scrollIndicator {
          animation: scrollIndicator 2s infinite;
        }
        .animate-float {
          animation: float 20s infinite ease-in-out;
        }
        .bg-grid-gray-200\/50 {
          background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23e5e7eb' fill-opacity='0.5' fill-rule='evenodd'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z'/%3E%3C/g%3E%3C/svg%3E");
        }
        .bg-grid-gray-800\/20 {
          background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%231f2937' fill-opacity='0.2' fill-rule='evenodd'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z'/%3E%3C/g%3E%3C/svg%3E");
        }
        
        /* Custom scrollbar styles */
        .scrollbar-thin::-webkit-scrollbar {
          height: 6px;
        }
        .scrollbar-thin {
          scrollbar-color: #374151 transparent;
          scrollbar-width: thin;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .dark .scrollbar-thin {
          scrollbar-color: #E5E7EB transparent;
        }
        .dark .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #E5E7EB;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default React.memo(Home);